// ═══════════════════════════════════════════════════════════════
// MainStreet — Facilities CRUD
// Table with org filter, modal for create/edit
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useMainStreetStore } from '../../store/mainstreetStore';
import type { MSFacility } from '../../store/mainstreetStore';
import { BUILDING_TYPES } from './constants';

const BUILDING_SELECT = BUILDING_TYPES.map(b => ({ value: b.id, label: b.label }));

const EMPTY: Partial<MSFacility> = {
  orgId: '', name: '', type: '', address: '', city: '', state: '', zip: '',
  buildingType: '', sqft: '', stories: '', occDay: '', occNight: '',
  hasGen: 'false', genDetails: '', hasHazmat: 'false', hazmatDetails: '',
  notes: '', coordinates: '',
};

export default function Facilities() {
  const {
    organizations, facilities, loadingFacilities,
    loadOrganizations, loadFacilities,
    createFacility, updateFacility, deleteFacility,
  } = useMainStreetStore();

  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState('all');

  const { orgLists, loadOrgLists } = useMainStreetStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MSFacility | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MSFacility | null>(null);
  const [form, setForm] = useState<Partial<MSFacility>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOrganizations();
    loadFacilities();
    loadOrgLists();
  }, []);

  const orgSelect = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.name || `Org #${o.id}` })),
  [organizations]);

  const orgFilterSelect = useMemo(() =>
    [{ value: 'all', label: 'All Organizations' }, ...orgSelect],
  [orgSelect]);

  const listOrgIds = useMemo(() => {
    if (listFilter === 'all') return null;
    const l = orgLists.find(x => String(x.id) === listFilter);
    return l ? new Set(l.orgIds.split(',').filter(Boolean)) : null;
  }, [listFilter, orgLists]);

  const listFilterSelect = useMemo(() => [
    { value: 'all', label: 'All Lists' },
    ...orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length})` })),
  ], [orgLists]);

  const filtered = useMemo(() => {
    let list = facilities;
    if (listOrgIds) list = list.filter(f => listOrgIds.has(f.orgId));
    if (orgFilter && orgFilter !== 'all') list = list.filter(f => f.orgId === orgFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(f =>
        f.name.toLowerCase().includes(q) ||
        f.address.toLowerCase().includes(q) ||
        f.city.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [facilities, orgFilter, search]);

  const orgName = (id: string) => organizations.find(o => String(o.id) === id)?.name ?? id;

  const setField = (key: keyof MSFacility, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setModalOpen(true);
  };

  const openEdit = (fac: MSFacility) => {
    setEditTarget(fac);
    setForm({ ...fac });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'Facility name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateFacility(editTarget.id, form);
        notifications.show({ message: 'Facility updated', color: 'green' });
      } else {
        await createFacility(form);
        notifications.show({ message: 'Facility created', color: 'green' });
      }
      setModalOpen(false);
      setEditTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteFacility(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Facilities</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingFacilities} onClick={() => loadFacilities()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Facility</Button>
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search name, address, city..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={orgFilterSelect} value={orgFilter ?? 'all'}
          onChange={v => setOrgFilter(v)} size="xs" w={220} />
        {orgLists.length > 0 && (
          <Select data={listFilterSelect} value={listFilter}
            onChange={v => setListFilter(v ?? 'all')} size="xs" w={200} />
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} facilit{filtered.length !== 1 ? 'ies' : 'y'}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Organization</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Address</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Building</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Sq Ft</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Stories</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Occupancy</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Gen</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Hazmat</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loadingFacilities ? 'Loading...' : 'No facilities found'}
                  </td>
                </tr>
              )}
              {filtered.map(fac => (
                <tr key={fac.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                  onClick={() => openEdit(fac)}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{fac.name || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{orgName(fac.orgId)}</td>
                  <td style={{ padding: '8px 12px' }}>{fac.type || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{[fac.address, fac.city, fac.state].filter(Boolean).join(', ') || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {BUILDING_TYPES.find(b => b.id === fac.buildingType)?.label ?? fac.buildingType ?? '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{fac.sqft || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fac.stories || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fac.occDay ? `${fac.occDay} day` : ''}{fac.occDay && fac.occNight ? ' / ' : ''}{fac.occNight ? `${fac.occNight} night` : ''}{!fac.occDay && !fac.occNight ? '-' : ''}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    {(() => { const v = (fac.hasGen || '').toLowerCase(); const yes = v === 'true' || v === 'yes'; return <Badge size="xs" color={yes ? 'green' : 'gray'}>{yes ? 'Yes' : 'No'}</Badge>; })()}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    {(() => { const v = (fac.hasHazmat || '').toLowerCase(); const yes = v === 'true' || v === 'yes'; return <Badge size="xs" color={yes ? 'red' : 'gray'}>{yes ? 'Yes' : 'No'}</Badge>; })()}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={(e) => { e.stopPropagation(); openEdit(fac); }}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={(e) => { e.stopPropagation(); setDeleteTarget(fac); }}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Facility' : 'Add Facility'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Organization" data={orgSelect} value={form.orgId || null}
              onChange={v => setField('orgId', v ?? '')} searchable clearable />
            <TextInput label="Name" required value={form.name ?? ''}
              onChange={e => setField('name', e.currentTarget.value)} />
            <TextInput label="Type" value={form.type ?? ''}
              onChange={e => setField('type', e.currentTarget.value)} />
            <Select label="Building Type" data={BUILDING_SELECT} value={form.buildingType || null}
              onChange={v => setField('buildingType', v ?? '')} clearable />
            <TextInput label="Address" value={form.address ?? ''}
              onChange={e => setField('address', e.currentTarget.value)} />
            <TextInput label="City" value={form.city ?? ''}
              onChange={e => setField('city', e.currentTarget.value)} />
            <TextInput label="State" value={form.state ?? ''}
              onChange={e => setField('state', e.currentTarget.value)} />
            <TextInput label="ZIP" value={form.zip ?? ''}
              onChange={e => setField('zip', e.currentTarget.value)} />
            <TextInput label="Sq Ft" value={form.sqft ?? ''}
              onChange={e => setField('sqft', e.currentTarget.value)} />
            <TextInput label="Stories" value={form.stories ?? ''}
              onChange={e => setField('stories', e.currentTarget.value)} />
            <TextInput label="Occ. Day" value={form.occDay ?? ''}
              onChange={e => setField('occDay', e.currentTarget.value)} />
            <TextInput label="Occ. Night" value={form.occNight ?? ''}
              onChange={e => setField('occNight', e.currentTarget.value)} />
          </SimpleGrid>

          <Group>
            <Switch label="Has Generator" checked={form.hasGen === 'true'}
              onChange={e => setField('hasGen', e.currentTarget.checked ? 'true' : 'false')} />
            <Switch label="Has Hazmat" checked={form.hasHazmat === 'true'}
              onChange={e => setField('hasHazmat', e.currentTarget.checked ? 'true' : 'false')} />
          </Group>

          {form.hasGen === 'true' && (
            <TextInput label="Generator Details" value={form.genDetails ?? ''}
              onChange={e => setField('genDetails', e.currentTarget.value)} />
          )}
          {form.hasHazmat === 'true' && (
            <TextInput label="Hazmat Details" value={form.hazmatDetails ?? ''}
              onChange={e => setField('hazmatDetails', e.currentTarget.value)} />
          )}

          <TextInput label="Coordinates" value={form.coordinates ?? ''}
            onChange={e => setField('coordinates', e.currentTarget.value)}
            placeholder="Auto-geocoded from address" description="Leave blank to auto-geocode from address" />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Facility'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Facility" centered size="sm">
        <Stack>
          <Text size="sm">Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
